import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Button from 'components/Button';
import FluidImage from 'components/FluidImage';
import Icon from 'components/Icon';
import ProjectCardSlider from 'components/ProjectCardSlider';

import { presentDomain } from 'utils';

import css from './ProjectCard.module.scss';

class ProjectCard extends React.PureComponent {
    static propTypes = {
        id: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]).isRequired,
        image: PropTypes.string,
        name: PropTypes.string.isRequired,
        address: PropTypes.string,
        location: PropTypes.string,
        division: PropTypes.string,
        projectStatusLabel: PropTypes.string,
        projectStart: PropTypes.string,
        projectStartFormatted: PropTypes.string,
        projectEnd: PropTypes.string,
        projectEndFormatted: PropTypes.string,
        link: PropTypes.string,
        text: PropTypes.string,
        externalUrl: PropTypes.string,
        useExternalUrl: PropTypes.bool.isRequired,
        onMouseEnter: PropTypes.func,
        onMouseLeave: PropTypes.func,
        view: PropTypes.oneOf([
            "default", "grid", "list"
        ]).isRequired,
        compact: PropTypes.bool.isRequired,
        onLocationClick: PropTypes.func,
        features: PropTypes.array,
        region: PropTypes.string,
    }

    static defaultProps = {
        useExternalUrl: false,
        view: "default",
        compact: false,
    }

    _getProjectUrl = () => {
        const { view, link, externalUrl, useExternalUrl } = this.props;

        // We don't need a link when the component
        // is not a <a> tag
        if(view !== 'default') return null;

        if(useExternalUrl && externalUrl) return externalUrl;

        return link;
    }

    render() {
        const {
            id,
            image,
            name,
            link,
            division,
            text,
            onMouseEnter,
            onMouseLeave,
            view,
            compact,
            onLocationClick,
            projectStatusLabel,
            projectStart,
            projectEnd,
            projectStartFormatted,
            projectEndFormatted,
            features,
            region,
            useExternalUrl,
            externalUrl,
        } = this.props;

        // If we display the project in the ProjectFilter
        // we don't want the card to be a link
        let Component = "a";
        if (view !== 'default' || useExternalUrl) {
            Component = "div";
        }

        return (
            <Component
                className={cn("card", css.card, {
                    [css.compact]: compact,
                    [css.list]: view === 'list',
                    [css.hasImage]: !!image,
                })}
                data-project-id={id}
                href={Component === "a" && this._getProjectUrl()}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {view === 'grid' && (
                    <ProjectCardSlider
                        compact={compact}
                        image={image}
                        features={features}
                        projectStatusLabel={projectStatusLabel}
                        projectEnd={projectEnd}
                        projectEndFormatted={projectEndFormatted}
                        projectStart={projectStart}
                        projectStartFormatted={projectStartFormatted}
                    />
                )}
                {(view !== 'grid' && image) && (
                    <div className={cn("image-wrapper", css.imageWrapper)}>
                        <FluidImage
                            altText={name}
                            className={css.image}
                            image={image}
                        />
                    </div>
                )}
                <div className={cn("info", css.info)}>
                    {view === 'default'
                        ? <p className={css.name}>{name}</p>
                        : <a className={css.name} href={link}>{name}</a>
                    }
                    {view === 'default' && ([
                        <p className={cn("address", css.address)} key="address">
                            {division || String.fromCharCode(160)}
                        </p>,
                        (text) && <p className={cn("text", css.text)} key="text">
                            {text}
                        </p>
                    ])}
                    {view !== 'default' && (
                        <ul className={css.details}>
                            {division && (
                                <li className={css.division}>
                                    <Icon icon="briefcase" />
                                    {division}
                                </li>
                            )}
                            {region && (
                                <li className={css.region}>
                                    <Icon icon="location" />
                                    {onLocationClick
                                        ? <button data-project={id} onClick={onLocationClick}>{region}</button>
                                        : <span>{region}</span>
                                    }
                                </li>
                            )}
                        </ul>
                    )}
                    {useExternalUrl && externalUrl && (
                        <Button
                            block
                            className={css.externalUrl}
                            href={externalUrl}
                            secondary
                        >{presentDomain(externalUrl)}</Button>
                    )}
                </div>
            </Component>
        );
    }
}

export default ProjectCard;



// WEBPACK FOOTER //
// ./src/components/ProjectCard/ProjectCard.jsx